import React, { useState, useEffect } from "react";
import { ref, get, child, set } from "firebase/database";
import { database } from "../../firebase";
import TopBar from "../Sidebar/SidebarUser";
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Snackbar,
} from "@mui/material";

const Profile = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    organization: "",
  });

  // Fetch user profile
  useEffect(() => {
    const fetchUserProfile = async () => {
      const storedUser = JSON.parse(localStorage.getItem("currentUser"));

      if (!storedUser) {
        setError("User not logged in.");
        setLoading(false);
        return;
      }

      try {
        const dbRef = ref(database);
        const snapshot = await get(child(dbRef, `users`));

        if (snapshot.exists()) {
          const usersData = snapshot.val();
          const userProfile = Object.values(usersData).find(
            (u) => u.email && u.email.toLowerCase() === storedUser.email.toLowerCase()
          );

          if (userProfile) {
            setUser(userProfile);
            setFormData({
              first_name: userProfile.first_name,
              last_name: userProfile.last_name,
              email: userProfile.email,
              phone: userProfile.phone,
              organization: userProfile.organization,
            });
          } else {
            setError("Profile not found.");
          }
        } else {
          setError("No users found.");
        }
      } catch (err) {
        setError("Failed to load profile. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  // Save profile changes
  const handleSave = async () => {
    setLoading(true);
    try {
      const storedUser = JSON.parse(localStorage.getItem("currentUser"));

      if (!storedUser) {
        setError("User not logged in.");
        setLoading(false);
        return;
      }

      const dbRef = ref(database, `users/${storedUser.fingerprint_id}`);
      await set(dbRef, { ...user, ...formData });

      setUser((prev) => ({ ...prev, ...formData }));
      setEditMode(false);
      setError("");
      setSuccess(true);
    } catch (err) {
      setError("Failed to save changes. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: "#f7f9fc", minHeight: "100vh", pt: 8, pb: 4 }}>
      <TopBar />
      <Box sx={{ maxWidth: "600px", margin: "0 auto", padding: "20px" }}>
        <Typography variant="h3" align="center" sx={{ mb: 4, fontWeight: "bold" }}>
          Profile
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Snackbar
            open={success}
            autoHideDuration={3000}
            onClose={() => setSuccess(false)}
            message="Profile updated successfully!"
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          />
        )}
        {user && (
          <Card sx={{ borderRadius: "15px", boxShadow: 3, padding: "20px" }}>
            <CardContent>
              <Box sx={{ mb: 3 }}>
                <TextField
                  label="First Name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  fullWidth
                  disabled={!editMode}
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="Last Name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  fullWidth
                  disabled={!editMode}
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="Email"
                  name="email"
                  value={formData.email}
                  fullWidth
                  disabled
                  sx={{ mb: 2, backgroundColor: "#f9f9f9", borderRadius: "4px" }}
                />
                <TextField
                  label="Phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  fullWidth
                  disabled={!editMode}
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="Organization"
                  name="organization"
                  value={formData.organization}
                  onChange={handleInputChange}
                  fullWidth
                  disabled={!editMode}
                />
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                {!editMode ? (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setEditMode(true)}
                    fullWidth
                    sx={{ fontWeight: "bold" }}
                  >
                    Edit Profile
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="contained"
                      sx={{
                        width: "48%",
                        fontWeight: "bold",
                        backgroundColor: "#A8E9F3",
                        "&:hover": {
                          backgroundColor: "#A8E9F3",
                        },
                      }}
                      onClick={handleSave}
                    >
                      Save
                    </Button>
                    <Button
                      variant="contained"
                      sx={{
                        width: "48%",
                        fontWeight: "bold",
                        backgroundColor: "red",
                        "&:hover": {
                          backgroundColor: "darkred",
                        },
                      }}
                      onClick={() => setEditMode(false)}
                    >
                      Cancel
                    </Button>
                  </>
                )}
              </Box>
            </CardContent>
          </Card>
        )}
      </Box>
    </Box>
  );
};

export default Profile;
